import type { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react';

type Variant = 'solid' | 'inverse' | 'accent' | 'outline';
type Size = 'sm' | 'lg';

// `base` fixes display to inline-flex. ClassName-based `hidden`/`sm:block` etc.
// passed by a caller can lose to it (utility order isn't guaranteed by JSX
// source order) — toggle visibility on a wrapping element instead.
const base =
  'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current disabled:pointer-events-none disabled:opacity-50';

const variantClasses: Record<Variant, string> = {
  solid: 'bg-ink text-white hover:bg-ink/90',
  inverse: 'bg-canvas text-surface-dark hover:bg-white',
  accent: 'bg-accent text-white hover:bg-accent/90',
  outline: 'border border-current bg-transparent hover:bg-white/10',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-10 gap-2 rounded-[10px] px-4 text-base',
  lg: 'h-14 gap-2 rounded-xl px-4 py-3 text-lg',
};

interface CommonProps {
  variant?: Variant;
  size?: Size;
}

type ButtonAsButton = CommonProps & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };

type ButtonAsAnchor = CommonProps & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export function Button({ variant = 'solid', size = 'lg', className = '', ...props }: ButtonProps) {
  const classes = `${base} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  if (props.href !== undefined) {
    return <a className={classes} {...props} />;
  }

  return <button type="button" className={classes} {...props} />;
}
